import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { CalendarDays, PlayCircle, AlertCircle, BadgeCheck, ArrowRight } from "lucide-react";

const STATUTS = {
  ACTIF: { label: "Adhésion active", cls: "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30" },
  PROSPECT: { label: "Prospect", cls: "bg-cfhtb-secondary/10 text-cfhtb-secondary border-cfhtb-secondary/30" },
  EXPIRE: { label: "Adhésion expirée", cls: "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30" },
  SUSPENDU: { label: "Suspendu", cls: "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30" },
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function MemberDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api("/membre/dashboard").then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <AppLayout>
        <p className="text-cfhtb-error text-sm" data-testid="dashboard-error">{error}</p>
      </AppLayout>
    );
  }
  if (!data) {
    return (
      <AppLayout>
        <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
      </AppLayout>
    );
  }

  const { person, adhesion, seminaires_a_venir, mes_inscriptions, replays, historique, rappel_fiche_praticien } = data;
  const statut = STATUTS[person.statut] || STATUTS.PROSPECT;

  return (
    <AppLayout>
      <div data-testid="member-dashboard" className="space-y-8 animate-fade-in">
        <div className="bg-gradient-to-br from-white via-white to-cfhtb-bg rounded-lg border-l-4 border-l-cfhtb-primary border border-slate-200 shadow-sm p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-cfhtb-heading tracking-tight">
                Bonjour {person.prenom || ""} {person.nom || ""}
              </h1>
              <p className="text-cfhtb-text mt-2 text-sm sm:text-base">
                Bienvenue dans votre espace adhérent CFHTB.
              </p>
            </div>
            <div className="text-right">
              <span
                data-testid="member-status-badge"
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border ${statut.cls}`}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                {statut.label}
              </span>
              {adhesion.date_fin && (
                <p className="text-xs text-cfhtb-text mt-2">
                  Échéance : {formatDate(adhesion.date_fin)}
                </p>
              )}
            </div>
          </div>
          {rappel_fiche_praticien && (
            <div className="mt-5 flex items-start gap-3 rounded-md bg-cfhtb-warning/10 border border-cfhtb-warning/30 p-4" data-testid="profile-reminder">
              <AlertCircle className="h-5 w-5 text-cfhtb-warning shrink-0 mt-0.5" />
              <div className="text-sm text-cfhtb-text">
                <p className="font-medium text-cfhtb-heading">Votre fiche praticien est incomplète</p>
                <p className="mt-0.5">
                  Complétez votre fiche pour apparaître dans l'annuaire des praticiens.{" "}
                  <Link to="/profil" className="text-cfhtb-secondary underline">Compléter ma fiche</Link>
                </p>
              </div>
            </div>
          )}
        </div>

        {mes_inscriptions && mes_inscriptions.length > 0 && (
          <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6" data-testid="my-enrollments-section">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="h-5 w-5 text-cfhtb-primary" />
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Mes inscriptions</h2>
            </div>
            <ul className="divide-y divide-slate-100" data-testid="my-enrollments-list">
              {mes_inscriptions.map((s) => (
                <li key={`${s.slug}-${s.date_debut}`} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-cfhtb-heading text-sm">{s.titre}</p>
                    <p className="text-xs text-cfhtb-text mt-0.5">
                      {s.date_debut ? formatDate(s.date_debut) : "Date à venir"} · {s.type || "événement"}
                    </p>
                  </div>
                  {s.statut === "PUBLIE" && s.slug && (
                    <Link to={`/seminaires/${s.slug}`} className="shrink-0 text-xs font-medium text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
                      Voir
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="h-5 w-5 text-cfhtb-primary" />
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Prochains événements</h2>
            </div>
            {seminaires_a_venir.length === 0 ? (
              <p className="text-sm text-cfhtb-text">Aucun événement programmé pour le moment.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="upcoming-seminars-list">
                {seminaires_a_venir.map((s) => (
                  <li key={s.id} className="py-4 first:pt-0 last:pb-0 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-cfhtb-heading text-sm">{s.titre}</p>
                      <p className="text-xs text-cfhtb-text mt-1">
                        {formatDate(s.date_debut)} · {s.type}
                      </p>
                    </div>
                    {s.lien_billetterie_externe && (
                      <a
                        href={s.lien_billetterie_externe}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-cfhtb-secondary hover:text-cfhtb-primary transition-colors"
                      >
                        Billetterie <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle className="h-5 w-5 text-cfhtb-secondary" />
              <h2 className="font-serif text-xl font-semibold text-cfhtb-heading">Mes replays</h2>
            </div>
            {replays.length === 0 ? (
              <p className="text-sm text-cfhtb-text" data-testid="no-replays-message">
                Après inscription, les replays sont disponibles dès leur mise en ligne.
              </p>
            ) : (
              <ul className="space-y-3" data-testid="replays-list">
                {replays.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={`/seminaires/${r.slug}`}
                      data-testid={`replay-link-${r.slug}`}
                      className="block rounded-md border border-cfhtb-light/30 bg-cfhtb-bg/50 p-3 hover:border-cfhtb-secondary hover:shadow-sm transition-all group"
                    >
                      <p className="text-sm font-medium text-cfhtb-heading group-hover:text-cfhtb-primary transition-colors">{r.titre}</p>
                      <p className="text-xs text-cfhtb-text mt-0.5">
                        Accès jusqu'au {formatDate(r.replay_expire_le)}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {historique.length > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider font-medium text-cfhtb-text/80 mb-2">
                  Historique
                </p>
                <ul className="space-y-1.5">
                  {historique.slice(0, 4).map((h) => (
                    <li key={`${h.slug}-${h.date_debut}`} className="text-xs text-cfhtb-text">
                      {h.titre} — {formatDate(h.date_debut)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
